// =====================================================
// COMPOSANT: OriginSection — « Origine des PI » (RUN 5)
// =====================================================
// VISUEL barres horizontales (hero — Maxime préfère le visuel) des canaux
// d'origine des pré-inscrits + tableau récap (Canal | PI | Part | N-1) en
// SECONDAIRE (repliable ; le N-1 origine est « moins intéressant »). PAS de
// carte de France (déjà dans l'espace participant). Recharts via ChartContainer
// maison. Honnête sur la couverture origin_id du seed. Design system crm-4l.

import { useState } from 'react';
import { Bar, BarChart, Cell, LabelList, XAxis, YAxis } from 'recharts';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { ChartContainer, type ChartConfig } from '@/components/ui/chart';
import type { OriginData, OriginSlice } from '@/lib/dashboard-stats';

/** Formatte une part 0..1 en « 34,2 % ». */
function fmtShare(v: number): string {
  return `${(v * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`;
}

/** Δ N-1 en absolu (count − prevCount), coloré. null → rien. */
function N1Delta({ slice }: { slice: OriginSlice }) {
  if (slice.prevCount == null) return <span className="text-zinc-400">—</span>;
  const delta = slice.count - slice.prevCount;
  const sign = delta > 0 ? '+' : '';
  const color = delta > 0 ? 'text-green-600' : delta < 0 ? 'text-red-600' : 'text-zinc-500';
  return (
    <span className="tabular-nums text-zinc-600">
      {slice.prevCount.toLocaleString('fr-FR')}{' '}
      <span className={color}>
        ({sign}
        {delta.toLocaleString('fr-FR')})
      </span>
    </span>
  );
}

/** Barres horizontales des origines (hero). */
function OriginBars({ slices }: { slices: OriginSlice[] }) {
  // Hauteur adaptée au nombre de canaux (barres lisibles).
  const height = Math.max(160, slices.length * 44);
  const config: ChartConfig = { count: { label: 'PI', color: '#18181b' } };

  return (
    <ChartContainer config={config} className="aspect-auto w-full" style={{ height }}>
      <BarChart data={slices} layout="vertical" margin={{ top: 4, right: 44, left: 8, bottom: 4 }}>
        <XAxis type="number" hide allowDecimals={false} />
        <YAxis
          type="category"
          dataKey="label"
          tickLine={false}
          axisLine={false}
          width={140}
          tick={{ fontSize: 12, fill: '#3f3f46' }}
        />
        <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={26}>
          {slices.map((s) => (
            <Cell key={s.originId ?? '__unknown__'} fill={s.color} />
          ))}
          <LabelList
            dataKey="count"
            position="right"
            className="fill-zinc-700"
            style={{ fontSize: 12, fontWeight: 600 }}
            formatter={(v: number) => v.toLocaleString('fr-FR')}
          />
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}

interface OriginSectionProps {
  origin: OriginData;
  showN1: boolean;
}

export function OriginSection({ origin, showN1 }: OriginSectionProps) {
  const [tableOpen, setTableOpen] = useState(false);
  const { slices, totalPi, piWithOrigin, coverage, hasN1 } = origin;
  const showN1Col = showN1 && hasN1;

  const isEmpty = totalPi === 0 || slices.every((s) => s.count === 0);

  return (
    <section className="flex flex-col gap-6">
      {/* Titre de section */}
      <div className="flex items-baseline gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-700">
          Origine des PI
        </h2>
        <div className="h-px flex-1 bg-zinc-200" />
      </div>

      <Card className="border border-zinc-200">
        <CardContent className="flex flex-col gap-4 px-6">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="flex flex-col">
              <h3 className="text-sm font-semibold text-zinc-950">
                Canaux d'acquisition des pré-inscrits
              </h3>
              <span className="text-xs text-zinc-500">
                {totalPi.toLocaleString('fr-FR')} pré-inscrits sur l'édition
              </span>
            </div>
          </div>

          {isEmpty ? (
            <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-zinc-200 text-xs text-zinc-400">
              Aucun pré-inscrit sur la période.
            </div>
          ) : (
            <>
              {/* VISUEL — barres horizontales (hero) */}
              <OriginBars slices={slices} />

              {/* Note de couverture (honnêteté seed) */}
              {coverage != null && coverage < 1 ? (
                <p className="inline-flex items-start gap-1.5 text-xs leading-relaxed text-zinc-500">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-400" />
                  <span>
                    Origine renseignée pour {piWithOrigin.toLocaleString('fr-FR')} PI sur{' '}
                    {totalPi.toLocaleString('fr-FR')} ({fmtShare(coverage)}). Les PI sans origine
                    apparaissent en « Origine inconnue ».
                  </span>
                </p>
              ) : null}

              {/* TABLEAU RÉCAP — secondaire, repliable (N-1 « moins intéressant ») */}
              <div className="overflow-hidden rounded-xl border border-zinc-200">
                <button
                  type="button"
                  onClick={() => setTableOpen((o) => !o)}
                  aria-expanded={tableOpen}
                  className="flex w-full items-center justify-between gap-2 bg-zinc-50 px-3 py-2 text-left transition-colors hover:bg-zinc-100"
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-zinc-600">
                    Tableau récap {showN1Col ? '(avec N-1)' : ''}
                  </span>
                  {tableOpen ? (
                    <ChevronDown className="h-4 w-4 text-zinc-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-zinc-400" />
                  )}
                </button>
                {tableOpen ? (
                  <table className="w-full border-collapse">
                    <thead className="bg-white">
                      <tr className="text-[11px] uppercase tracking-wide text-zinc-500">
                        <th className="py-2 pl-3 pr-2 text-left font-semibold">Canal</th>
                        <th className="py-2 pr-3 text-right font-semibold">PI</th>
                        <th className="py-2 pr-3 text-right font-semibold">Part</th>
                        {showN1Col ? (
                          <th className="py-2 pr-3 text-right font-semibold">N-1</th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody>
                      {slices.map((s) => (
                        <tr key={s.originId ?? '__unknown__'} className="border-t border-zinc-100">
                          <td className="py-2 pl-3 pr-2">
                            <span className="inline-flex items-center gap-2 text-sm text-zinc-800">
                              <span
                                className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
                                style={{ backgroundColor: s.color }}
                              />
                              {s.label}
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-right text-sm tabular-nums text-zinc-800">
                            {s.count.toLocaleString('fr-FR')}
                          </td>
                          <td className="py-2 pr-3 text-right text-sm tabular-nums text-zinc-600">
                            {fmtShare(s.share)}
                          </td>
                          {showN1Col ? (
                            <td className="py-2 pr-3 text-right text-sm">
                              <N1Delta slice={s} />
                            </td>
                          ) : null}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : null}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
